import React, { useCallback, useEffect, useState } from 'react';
import VisitantesTable from './VisitantesTable';
import VisitanteModal from './VisitanteModal';
import { ChevronLeft, ChevronRight, UserPlus, Users, DoorOpen, History } from 'lucide-react';
import api from '../../services/api';
import { PageHeader, PageLayout, SearchField, SummaryCard, SummaryGrid } from '../../components/ui/PageLayout';

const PAGE_SIZE = 25;

const VisitantesPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visitantes, setVisitantes] = useState([]);
  const [moradores, setMoradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMoradores, setLoadingMoradores] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [visitanteParaEditar, setVisitanteParaEditar] = useState(null);
  const [statusFilter, setStatusFilter] = useState('todos');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    total_presentes: 0,
    total_hoje: 0,
    total_filtrado: 0,
    total_pages: 1,
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      setPage(1);
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  const fetchVisitantes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/visitantes/paginado', {
        params: {
          page,
          limit: PAGE_SIZE,
          search: debouncedSearchTerm || undefined,
          status: statusFilter,
        },
      });
      const data = response.data || {};
      const totalPages = data.total_pages || 1;

      setVisitantes(data.items || []);
      setPagination({
        total: data.total || 0,
        total_presentes: data.total_presentes || 0,
        total_hoje: data.total_hoje || 0,
        total_filtrado: data.total_filtrado || 0,
        total_pages: totalPages,
      });

      if (page > totalPages) {
        setPage(totalPages);
      }
    } catch (error) {
      console.error('Erro ao buscar visitantes:', error);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, page, statusFilter]);

  const fetchMoradores = useCallback(async () => {
    if (moradores.length || loadingMoradores) return;

    setLoadingMoradores(true);
    try {
      const response = await api.get('/visitantes/moradores');
      setMoradores(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar moradores:', error);
    } finally {
      setLoadingMoradores(false);
    }
  }, [loadingMoradores, moradores.length]);

  useEffect(() => {
    fetchVisitantes();
  }, [fetchVisitantes]);

  const openModal = async (visitante = null) => {
    setVisitanteParaEditar(visitante);
    setIsModalOpen(true);
    await fetchMoradores();
  };

  const closeModal = async () => {
    setIsModalOpen(false);
    setVisitanteParaEditar(null);
    await fetchVisitantes();
  };

  const handleStatusFilterChange = (nextStatus) => {
    setStatusFilter((current) => (current === nextStatus && nextStatus !== 'todos' ? 'todos' : nextStatus));
    setPage(1);
  };

  const firstItem = pagination.total_filtrado ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastItem = Math.min(page * PAGE_SIZE, pagination.total_filtrado);
  const canGoPrevious = page > 1;
  const canGoNext = page < pagination.total_pages;

  return (
    <PageLayout>
      <PageHeader
        title="Visitantes"
        description="Controle de entrada e saída de visitantes"
        actions={(
          <button
            onClick={() => openModal()}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-condo-dark px-5 py-2.5 font-medium text-white shadow-md transition hover:bg-slate-700 sm:w-auto"
          >
            <UserPlus size={20} /> Registrar Entrada
          </button>
        )}
      />

      <SummaryGrid>
        <SummaryCard
          title="Total de Visitas"
          value={pagination.total}
          color="blue"
          icon={<Users size={24} />}
          active={statusFilter === 'todos'}
          onClick={() => handleStatusFilterChange('todos')}
        />
        <SummaryCard
          title="Visitantes Presentes"
          value={pagination.total_presentes}
          color="green"
          icon={<DoorOpen size={24} />}
          active={statusFilter === 'presentes'}
          onClick={() => handleStatusFilterChange('presentes')}
        />
        <SummaryCard
          title="Visitas Hoje"
          value={pagination.total_hoje}
          color="purple"
          icon={<History size={24} />}
          active={statusFilter === 'hoje'}
          onClick={() => handleStatusFilterChange('hoje')}
        />
      </SummaryGrid>

      <SearchField
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Buscar visitante por nome ou documento..."
      />

      <VisitantesTable
        visitantes={visitantes}
        loading={loading}
        refreshData={fetchVisitantes}
        onEdit={(visitante) => openModal(visitante)}
      />

      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
        <span>
          Exibindo {firstItem}-{lastItem} de {pagination.total_filtrado} registro(s)
        </span>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={!canGoPrevious || loading}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Pagina anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="min-w-24 text-center font-semibold text-slate-700">
            Pagina {page} de {pagination.total_pages}
          </span>
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(pagination.total_pages, current + 1))}
            disabled={!canGoNext || loading}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            title="Proxima pagina"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <VisitanteModal
        isOpen={isModalOpen}
        onClose={closeModal}
        moradores={moradores}
        visitanteParaEditar={visitanteParaEditar}
      />
    </PageLayout>
  );
};

export default VisitantesPage;
